import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import './App.css';
import Notification from './components/Notification';
import Login from './components/Login';
import { useAuth } from './hooks/useAuth';

// const API_BASE = 'http://localhost:3001';
const API_BASE = 'https://control.53dnorth.com';

class RateLimiter {
  constructor() {
    this.requests = new Map();
  }

  canMakeRequest(key, maxRequests, interval) {
    const now = Date.now();
    const requestData = this.requests.get(key) || { count: 0, firstRequestTime: now };

    // Reset the count if the interval has passed
    if (now - requestData.firstRequestTime > interval) {
      requestData.count = 0;
      requestData.firstRequestTime = now;
    }

    if (requestData.count < maxRequests) {
      requestData.count++;
      this.requests.set(key, requestData);
      return true;
    }

    return false;
  }

  getRemainingTime(key) {
    const requestData = this.requests.get(key);
    if (!requestData) return 0;
    
    const elapsedTime = Date.now() - requestData.firstRequestTime;
    const remainingTime = Math.max(0, 60000 - elapsedTime);
    return remainingTime;
  }
}

const rateLimiter = new RateLimiter();

function App() {
  const { user, authToken, isAuthReady } = useAuth();
  const socketRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [servers, setServers] = useState({});
  const [selectedServer, setSelectedServer] = useState('');
  const [pm2Processes, setPm2Processes] = useState([]);
  const [systemdServices, setSystemdServices] = useState([]);
  const [dockerContainers, setDockerContainers] = useState([]); // Add Docker state
  const [logs, setLogs] = useState({});
  const [activeTails, setActiveTails] = useState(new Set());
  const [expandedLog, setExpandedLog] = useState(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const logContentRefs = useRef({});
  const [operationInProgress, setOperationInProgress] = useState(new Set());
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [activeTab, setActiveTab] = useState('pm2');
  const logContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState({
    pm2: false,
    systemd: false,
    docker: false
  });
  
  const [streamStatus, setStreamStatus] = useState(new Map());
  const [existingNotifications, setExistingNotifications] = useState(new Map());

  const showNotification = useCallback((message, type = 'success') => {
    const existing = existingNotifications.get(message);
    const now = Date.now();
    if (existing && (now - existing.timestamp) < 5000) {
      return existing.id;
    }

    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    
    setExistingNotifications(prev => {
      const newMap = new Map(prev);
      newMap.set(message, { id, timestamp: now });
      return newMap;
    });

    setTimeout(() => {
      setExistingNotifications(prev => {
        const newMap = new Map(prev);
        newMap.delete(message);
        return newMap;
      });
    }, 5000);

    return id;
  }, [existingNotifications]);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const handleApiError = useCallback((error, operation) => {
    console.error(`Error in ${operation}:`, error);
    
    if (error.message && error.message.includes('429')) {
      showNotification(`Rate Limited: Too many ${operation} requests. Please wait a moment before trying again.`, 'error');
    } else if (error.message && error.message.includes('Too many')) {
      showNotification(`Rate Limited: ${error.message}`, 'error');
    } else {
      showNotification(`Error: Failed to ${operation}: ${error.message}`, 'error');
    }
  }, [showNotification]);

  // Update fetchWithRetry to include dependency check
  const fetchWithRetry = async (url, options = {}, retries = 2) => {
    if (!isAuthReady || !authToken) {
      throw new Error('Not authenticated');
    }
    
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${authToken}`
    };

    for (let i = 0; i <= retries; i++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await fetch(url, {
          ...options,
          headers,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (response.status === 429) {
          const remainingTime = response.headers.get('Retry-After') || '60';
          throw new Error(`Rate limited. Please wait ${remainingTime} seconds.`);
        }
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout');
        }
        if (i === retries) throw error;
        
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
      }
    }
  };

  const fetchServers = useCallback(async () => {
    if (!isAuthReady || !authToken) {
      return;
    }

    if (!rateLimiter.canMakeRequest('fetch-servers', 10, 60000)) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime('fetch-servers') / 1000);
      showNotification(`Rate Limited: Please wait ${remainingTime} seconds before refreshing servers.`, 'warning');
      return;
    }

    try {
      const data = await fetchWithRetry(`${API_BASE}/api/servers`);
      setServers(data);
      
      if (!selectedServer && Object.keys(data).length > 0) {
        setSelectedServer(Object.keys(data)[0]);
      }
    } catch (error) {
      handleApiError(error, 'fetch servers');
    }
  }, [isAuthReady, authToken, showNotification, selectedServer, handleApiError]);

  const fetchPM2Processes = useCallback(async (serverId) => {
    if (!serverId || isLoading.pm2) return;
    
    const operationKey = `fetch-pm2-${serverId}`;
    if (operationInProgress.has(operationKey)) return;
    
    if (!rateLimiter.canMakeRequest(`pm2-${serverId}`, 5, 60000)) {
      return;
    }

    const isInitialLoad = pm2Processes.length === 0;
    
    try {
      setOperationInProgress(prev => new Set([...prev, operationKey]));
      
      if (isInitialLoad) {
        setIsLoading(prev => ({ ...prev, pm2: true }));
      }
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const data = await fetchWithRetry(`${API_BASE}/api/servers/${serverId}/pm2`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (Array.isArray(data)) {
        setPm2Processes(prev => {
          const currentProcesses = new Map(prev.map(p => [p.name, p]));
          const newProcesses = new Map(data.map(p => [p.name, p]));
          
          const result = prev.map(process => {
            const updatedProcess = newProcesses.get(process.name);
            return updatedProcess || process;
          });
          
          data.forEach(process => {
            if (!currentProcesses.has(process.name)) {
              result.push(process);
            }
          });
          
          return result.filter(p => newProcesses.has(p.name));
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        handleApiError(new Error('Request timeout'), 'fetch PM2 processes');
      } else {
        handleApiError(error, 'fetch PM2 processes');
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(prev => ({ ...prev, pm2: false }));
      }
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationKey);
        return newSet;
      });
    }
  }, [operationInProgress, handleApiError, isLoading.pm2, pm2Processes.length]);

  const fetchSystemdServices = useCallback(async (serverId) => {
    if (!serverId || isLoading.systemd) return;
    
    const operationKey = `fetch-systemd-${serverId}`;
    if (operationInProgress.has(operationKey)) return;
    
    if (!rateLimiter.canMakeRequest(`systemd-${serverId}`, 5, 60000)) {
      return;
    }
    
    try {
      setOperationInProgress(prev => new Set([...prev, operationKey]));
      setIsLoading(prev => ({ ...prev, systemd: true }));
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const data = await fetchWithRetry(`${API_BASE}/api/servers/${serverId}/systemd`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (Array.isArray(data)) {
        setSystemdServices(prev => {
          const newServices = [...prev];
          const updatedServiceMap = new Map(data.map(s => [s.name, s]));
          
          data.forEach((service, index) => {
            const existingIndex = newServices.findIndex(s => s.name === service.name);
            if (existingIndex !== -1) {
              newServices[existingIndex] = service;
            } else {
              newServices.push(service);
            }
          });
          
          return newServices.filter(s => updatedServiceMap.has(s.name));
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        handleApiError(new Error('Request timeout'), 'fetch systemd services');
      } else {
        handleApiError(error, 'fetch systemd services');
      }
    } finally {
      setIsLoading(prev => ({ ...prev, systemd: false }));
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationKey);
        return newSet;
      });
    }
  }, [operationInProgress, handleApiError, isLoading.systemd]);

  const fetchDockerContainers = useCallback(async (serverId) => {
    if (!serverId || isLoading.docker) return;
    
    const operationKey = `fetch-docker-${serverId}`;
    if (operationInProgress.has(operationKey)) return;
    
    try {
      setOperationInProgress(prev => new Set([...prev, operationKey]));
      setIsLoading(prev => ({ ...prev, docker: true }));
      
      const data = await fetchWithRetry(`${API_BASE}/api/servers/${serverId}/docker`);
      if (Array.isArray(data)) {
        setDockerContainers(data);
      }
    } catch (error) {
      handleApiError(error, 'fetch Docker containers');
    } finally {
      setIsLoading(prev => ({ ...prev, docker: false }));
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationKey);
        return newSet;
      });
    }
  }, [operationInProgress, handleApiError]);

  const restartPM2Process = useCallback(async (processName) => {
    if (!selectedServer) return;
    
    const operationKey = `restart-pm2-${processName}`;
    if (operationInProgress.has(operationKey)) {
      showNotification('Operation in Progress: Please wait for the current restart to complete.', 'warning');
      return;
    }
    
    if (!rateLimiter.canMakeRequest(`restart-pm2-${selectedServer}`, 3, 60000)) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime(`restart-pm2-${selectedServer}`) / 1000);
      showNotification(`Rate Limited: Please wait ${remainingTime} seconds before restarting more PM2 processes.`, 'warning');
      return;
    }
    
    setOperationInProgress(prev => new Set([...prev, operationKey]));
    
    try {
      const data = await fetchWithRetry(
        `${API_BASE}/api/servers/${selectedServer}/pm2/${processName}/restart`,
        { method: 'POST' }
      );
      if (data.success) {
        if (data.process) {
          setPm2Processes(prev => 
            prev.map(p => p.name === processName ? data.process : p)
          );
        } else {
          setTimeout(async () => {
            try {
              const result = await fetchWithRetry(`${API_BASE}/api/servers/${selectedServer}/pm2`);
              if (Array.isArray(result)) {
                const updatedProcess = result.find(p => p.name === processName);
                if (updatedProcess) {
                  setPm2Processes(prev => 
                    prev.map(p => p.name === processName ? updatedProcess : p)
                  );
                }
              }
            } catch (error) {
              handleApiError(error, `fetch updated status for ${processName}`);
            }
          }, 2000);
        }
      } else {
        showNotification(`Failed to restart ${processName}: ${data.error}`, 'error');
      }
    } catch (error) {
      handleApiError(error, `restart ${processName}`);
    } finally {
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationKey);
        return newSet;
      });
    }
  }, [selectedServer, operationInProgress, handleApiError, showNotification]);

  const restartSystemdService = async (serviceName) => {
    if (!selectedServer) return;
    
    const operationKey = `restart-systemd-${serviceName}`;
    if (operationInProgress.has(operationKey)) {
      showNotification('Operation in Progress: Please wait for the current restart to complete.', 'warning');
      return;
    }
    
    if (!rateLimiter.canMakeRequest(`restart-systemd-${selectedServer}`, 3, 60000)) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime(`restart-systemd-${selectedServer}`) / 1000);
      showNotification(`Rate Limited: Please wait ${remainingTime} seconds before restarting more systemd services.`, 'warning');
      return;
    }
    
    setOperationInProgress(prev => new Set([...prev, operationKey]));
    
    try {
      const data = await fetchWithRetry(
        `${API_BASE}/api/servers/${selectedServer}/systemd/${serviceName}/restart`,
        { method: 'POST' }
      );
      
      if (data.success) {
        showNotification(`Successfully restarted ${serviceName}`, 'success');
        setTimeout(async () => {
          try {
            const result = await fetchWithRetry(`${API_BASE}/api/servers/${selectedServer}/systemd`);
            if (Array.isArray(result)) {
              const updatedService = result.find(s => s.name === serviceName);
              if (updatedService) {
                setSystemdServices(prev => 
                  prev.map(s => s.name === serviceName ? updatedService : s)
                );
              }
            }
          } catch (error) {
            handleApiError(error, `fetch updated status for ${serviceName}`);
          }
        }, 2000);
      } else {
        showNotification(`Failed to restart ${serviceName}: ${data.error}`, 'error');
      }
    } catch (error) {
      handleApiError(error, `restart ${serviceName}`);
    } finally {
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationKey);
        return newSet;
      });
    }
  };

  const restartDockerContainer = async (containerName) => {
    if (!selectedServer) return;
    
    const operationKey = `restart-docker-${containerName}`;
    if (operationInProgress.has(operationKey)) {
      showNotification('Operation in Progress: Please wait for the current restart to complete.', 'warning');
      return;
    }
    
    if (!rateLimiter.canMakeRequest(`restart-docker-${selectedServer}`, 3, 60000)) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime(`restart-docker-${selectedServer}`) / 1000);
      showNotification(`Rate Limited: Please wait ${remainingTime} seconds before restarting more Docker containers.`, 'warning');
      return;
    }
    
    setOperationInProgress(prev => new Set([...prev, operationKey]));
    
    try {
      const data = await fetchWithRetry(
        `${API_BASE}/api/servers/${selectedServer}/docker/${containerName}/restart`,
        { method: 'POST' }
      );
      
      if (data.success) {
        showNotification(`Successfully restarted ${containerName}`, 'success');
        setTimeout(async () => {
          try {
            const result = await fetchWithRetry(`${API_BASE}/api/servers/${selectedServer}/docker`);
            if (Array.isArray(result)) {
              const updatedContainer = result.find(c => c.name === containerName);
              if (updatedContainer) {
                setDockerContainers(prev => 
                  prev.map(c => c.name === containerName ? updatedContainer : c)
                );
              }
            }
          } catch (error) {
            handleApiError(error, `fetch updated status for ${containerName}`);
          }
        }, 2000);
      } else {
        showNotification(`Failed to restart ${containerName}: ${data.error}`, 'error');
      }
    } catch (error) {
      handleApiError(error, `restart ${containerName}`);
    } finally {
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationKey);
        return newSet;
      });
    }
  };

  const startTail = useCallback((server, filePath) => {
    if (!socketRef.current || !server) return;
    
    const tailKey = `${server}||${filePath}`;
    
    setLogs(prev => ({
        ...prev,
        [tailKey]: ''
    }));
    
    setStreamStatus(prev => {
        const newStatus = new Map(prev);
        newStatus.set(tailKey, { status: 'connecting', timestamp: Date.now() });
        return newStatus;
    });
    
    setActiveTails(prev => new Set([...prev, tailKey]));
    
    if (!rateLimiter.canMakeRequest(`tail-${server}`, 3, 60000)) {
        const remainingTime = Math.ceil(rateLimiter.getRemainingTime(`tail-${server}`) / 1000);
        showNotification(`Rate Limited: Please wait ${remainingTime} seconds before starting more log streams.`, 'warning');
        setStreamStatus(prev => {
            const newStatus = new Map(prev);
            newStatus.set(tailKey, { status: 'error', error: 'Rate limited', timestamp: Date.now() });
            return newStatus;
        });
        return;
    }
    
    if (activeTails.size >= 5 && !activeTails.has(tailKey)) {
        showNotification('Too Many Streams: Please stop some existing log streams before starting new ones.', 'warning');
        setStreamStatus(prev => {
            const newStatus = new Map(prev);
            newStatus.set(tailKey, { status: 'error', error: 'Too many streams', timestamp: Date.now() });
            return newStatus;
        });
        return;
    }
    
    socketRef.current.emit('start-tail', {
      serverId: server,
      filePath: filePath
    });
  }, [activeTails, showNotification]);

  const stopTail = useCallback((filePath, containerId) => {
    if (!socketRef.current) return;
    
    const [serverId] = containerId ? containerId.split('||') : [selectedServer];
    const tailKey = `${serverId}||${filePath}`;
    
    socketRef.current.emit('stop-tail', { 
      serverId: serverId,
      filePath: filePath,
      tailKey: tailKey
    });
    
    setActiveTails(prev => {
      const newSet = new Set(prev);
      newSet.delete(tailKey);
      return newSet;
    });
    
    setLogs(prev => {
      const newLogs = { ...prev };
      delete newLogs[tailKey];
      return newLogs;
    });

    setStreamStatus(prev => {
      const newStatus = new Map(prev);
      newStatus.delete(tailKey);
      return newStatus;
    });
  }, [selectedServer]);

  const executeAction = useCallback(async (type, action, target) => {
    if (!selectedServer) return;
    
    const operationKey = `${type}-${action}-${target}`;
    if (operationInProgress.has(operationKey)) {
      showNotification('Operation in Progress: Please wait for the current operation to complete.', 'warning');
      return;
    }
    
    // Use a single rate limit key for all PM2 control actions
    const rateKey = type === 'pm2' && ['restart', 'start', 'stop'].includes(action) 
      ? `pm2-control-${selectedServer}`  // Shared key for PM2 control actions
      : `${type}-${action}-${selectedServer}`; // Other actions use specific keys
    
    // Higher limit for PM2 control actions
    const rateLimit = type === 'pm2' && ['restart', 'start', 'stop'].includes(action)
      ? { max: 15, window: 60000 }  // 15 control actions per minute
      : { max: 5, window: 60000 };  // Default limits for other actions
    
    if (!rateLimiter.canMakeRequest(rateKey, rateLimit.max, rateLimit.window)) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime(rateKey) / 1000);
      showNotification(`Rate Limited: Please wait ${remainingTime} seconds before performing more PM2 actions.`, 'warning');
      return;
    }

    setOperationInProgress(prev => new Set([...prev, operationKey]));
    
    try {
      socketRef.current.emit('execute-command', {
        serverId: selectedServer,
        type,
        command: action,
        target
      });
    } catch (error) {
      handleApiError(error, `execute ${type} ${action}`);
    } finally {
      setTimeout(() => {
        setOperationInProgress(prev => {
          const newSet = new Set(prev);
          newSet.delete(operationKey);
          return newSet;
        });
      }, 2000);
    }
  }, [selectedServer, operationInProgress, showNotification, handleApiError]);

  const renderPm2Actions = useCallback((process) => (
    <div className="service-actions">
      <button 
        onClick={() => executeAction('pm2', 'restart', process.name)}
        disabled={operationInProgress.has(`pm2-restart-${process.name}`)}
        className={operationInProgress.has(`pm2-restart-${process.name}`) ? 'loading' : ''}
      >
        Restart
      </button>
      <button 
        onClick={() => executeAction('pm2', 'stop', process.name)}
        disabled={operationInProgress.has(`pm2-stop-${process.name}`)}
      >
        Stop
      </button>
      <button 
        onClick={() => executeAction('pm2', 'start', process.name)}
        disabled={operationInProgress.has(`pm2-start-${process.name}`)}
      >
        Start
      </button>
      <button 
        onClick={() => startTail(selectedServer, `/home/${servers[selectedServer]?.username}/.pm2/logs/${process.name}-out.log`)}
        className="log-button"
      >
        View Logs
      </button>
    </div>
  ), [executeAction, operationInProgress, selectedServer, servers, startTail]);

  const renderDockerActions = useCallback((container) => (
    <div className="service-actions">
      <button 
        onClick={() => executeAction('docker', 'restart', container.name)}
        disabled={operationInProgress.has(`docker-restart-${container.name}`)}
        className={operationInProgress.has(`docker-restart-${container.name}`) ? 'loading' : ''}
      >
        Restart
      </button>
      <button 
        onClick={() => executeAction('docker', 'stop', container.name)}
        disabled={operationInProgress.has(`docker-stop-${container.name}`)}
      >
        Stop
      </button>
      <button 
        onClick={() => executeAction('docker', 'start', container.name)}
        disabled={operationInProgress.has(`docker-start-${container.name}`)}
      >
        Start
      </button>
      <button 
        onClick={() => startTail(selectedServer, `docker:${container.name}`)}
        className="log-button"
      >
        View Logs
      </button>
    </div>
  ), [executeAction, operationInProgress, selectedServer, startTail]);

  const toggleExpand = useCallback((containerId) => {
    setExpandedLog(expandedLog === containerId ? null : containerId);
  }, [expandedLog]);

  useEffect(() => {
    Object.entries(logs).forEach(([containerId, log]) => {
      const ref = logContentRefs.current[containerId];
      if (ref && autoScroll) {
        ref.scrollTop = ref.scrollHeight;
      }
    });
  }, [logs, autoScroll]);

  const handleLogScroll = useCallback((containerId, e) => {
    const element = e.target;
    const isScrolledToBottom = Math.abs(
      element.scrollHeight - element.scrollTop - element.clientHeight
    ) < 50;
    setAutoScroll(isScrolledToBottom);
  }, []);

  const stripAnsiCodes = useCallback((text) => {
    const ESC = String.fromCharCode(27);
    const parts = text.split(ESC + '[');
    let result = parts[0];
    
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      const codeEnd = part.indexOf('m');
      if (codeEnd >= 0) {
        result += part.substring(codeEnd + 1);
      } else {
        result += part;
      }
    }
    
    return result;
  }, []);

  const handleTailData = useCallback((data) => {
    const tailKey = `${data.server}||${data.file || 'unknown'}`;
    setLogs(prev => {
      const newLogs = { ...prev };
      const currentLog = newLogs[tailKey] || '';
      const cleanedData = stripAnsiCodes(data.data);
      
      if (currentLog.length > 100000) { // 100KB limit
        newLogs[tailKey] = currentLog.slice(-50000) + cleanedData;
      } else {
        newLogs[tailKey] = currentLog + cleanedData;
      }

      if (autoScroll && logContainerRef.current) {
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        
        requestAnimationFrame(() => {
          if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
          }
        });
      }
      
      return newLogs;
    });
  }, [autoScroll, stripAnsiCodes]);

  useEffect(() => {
    if (isAuthReady && authToken) {
      // Initialize socket with reconnection options
      socketRef.current = io(API_BASE, {
        auth: { token: authToken },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000
      });
      
      socketRef.current.on('connect', () => {
        console.log('[Socket] Connected to server');
        setConnectionStatus('connected');
      });

      socketRef.current.on('disconnect', (reason) => {
        console.log('[Socket] Disconnected:', reason);
        setConnectionStatus('disconnected');
        if (reason === 'io server disconnect') {
          // Server initiated disconnect, need manual reconnect
          socketRef.current.connect();
        }
        showNotification('Connection Lost: Lost connection to server. Attempting to reconnect...', 'warning');
      });

      socketRef.current.on('connect_error', (error) => {
        console.error('[Socket] Connection error:', error);
        setConnectionStatus('error');
        // Only show connection error notification if we're still not connected after 5 seconds
        setTimeout(() => {
          if (!socketRef.current?.connected) {
            showNotification('Connection Error: Unable to connect to server. Check your network connection.', 'error');
          }
        }, 5000);
      });

      socketRef.current.on('tail-data', handleTailData);

      socketRef.current.on('tail-error', (data) => {
        console.error('Tail error:', data);
        showNotification(`Log Tail Error: ${data.error || 'Unknown error occurred while tailing logs'}`, 'error');
        setStreamStatus(prev => {
          const newStatus = new Map(prev);
          newStatus.set(`${data.server}||${data.file}`, { status: 'error', error: data.error, timestamp: Date.now() });
          return newStatus;
        });
        stopTail(data.file);
      });

      socketRef.current.on('tail-timeout', (data) => {
        showNotification(`Log Stream Timeout: Log stream for ${data.server} has timed out and been closed.`, 'warning');
        setStreamStatus(prev => {
          const newStatus = new Map(prev);
          newStatus.set(`${data.server}||${data.file}`, { status: 'timeout', timestamp: Date.now() });
          return newStatus;
        });
        stopTail(data.file);
      });

      socketRef.current.on('tail-started', (data) => {
        console.log('Tail started:', data);
        setStreamStatus(prev => {
          const newStatus = new Map(prev);
          newStatus.set(`${data.server}||${data.file}`, { status: 'active', timestamp: Date.now() });
          return newStatus;
        });
      });

      socketRef.current.on('tail-closed', (data) => {
        console.log('Tail closed:', data);
        setStreamStatus(prev => {
          const newStatus = new Map(prev);
          for (const [key, value] of newStatus.entries()) {
            if (key.startsWith(`${data.server}||`)) {
              value.status = 'closed';
              value.timestamp = Date.now();
            }
          }
          return newStatus;
        });
      });

      return () => {
        if (socketRef.current) {
          socketRef.current.removeAllListeners();
          socketRef.current.close();
          socketRef.current = null;
        }
      };
    }
  }, [isAuthReady, authToken, showNotification, stopTail, stripAnsiCodes, handleTailData]);

  // Move loadingStateRef and fetchFunctionsRef declarations to top
  const loadingStateRef = useRef(isLoading);
  const fetchFunctionsRef = useRef({
    fetchPM2Processes,
    fetchSystemdServices,
    fetchDockerContainers
  });

  // Add initialization effect
  useEffect(() => {
    if (isAuthReady && authToken) {
      console.log('[Auth] Auth ready, initializing data fetching');
      fetchServers();
    } else {
      // Reset state when not authenticated
      setServers({});
      setSelectedServer('');
      setPm2Processes([]);
      setSystemdServices([]);
      setDockerContainers([]);
    }
  }, [isAuthReady, authToken, fetchServers]);

  // Update the periodic fetch effect
  useEffect(() => {
    if (!selectedServer || !isAuthReady || !authToken) return;
    
    let isSubscribed = true;
    
    const fetchData = async () => {
      if (!isSubscribed || !isAuthReady || !authToken) return;
      
      try {
        if (activeTab === 'pm2' && !loadingStateRef.current.pm2) {
          await fetchPM2Processes(selectedServer);
        } else if (activeTab === 'systemd' && !loadingStateRef.current.systemd) {
          await fetchSystemdServices(selectedServer);
        } else if (activeTab === 'docker' && !loadingStateRef.current.docker) {
          await fetchDockerContainers(selectedServer);
        }
      } catch (error) {
        console.error('Data fetch error:', error);
      }
    };

    // Initial fetch immediately when server changes
    fetchData();

    // Periodic refresh
    const periodicFetchInterval = setInterval(fetchData, 60*1000);

    return () => {
      isSubscribed = false;
      clearInterval(periodicFetchInterval);
    };
  }, [selectedServer, activeTab, isAuthReady, authToken, fetchPM2Processes, fetchSystemdServices, fetchDockerContainers]);

  // Update ref values when dependencies change
  useEffect(() => {
    loadingStateRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    fetchFunctionsRef.current = {
      fetchPM2Processes,
      fetchSystemdServices,
      fetchDockerContainers
    };
  }, [fetchPM2Processes, fetchSystemdServices, fetchDockerContainers]);

  const renderLogs = () => {
    return Object.entries(logs).map(([containerId, log]) => {
      const isExpanded = expandedLog === containerId;
      const [serverId, file] = containerId.split('||');
      const status = streamStatus.get(containerId) || { status: 'unknown' };

      return (
        <div key={containerId} className={`log-viewer ${isExpanded ? 'expanded' : ''} ${status.status}`}>
          <div className="log-header">
            <div className="log-title">
              <strong>{file || 'unknown'}</strong>
              <span className={`stream-status ${status.status}`}>
                {status.status === 'active' ? 'Live' : 
                 status.status === 'error' ? 'Error' :
                 status.status === 'connecting' ? 'Connecting...' :
                 status.status === 'timeout' ? 'Timed Out' :
                 status.status === 'closed' ? 'Disconnected' : 'Unknown'}
              </span>
              {status.error && <span className="error-message">({status.error})</span>}
            </div>
            <div className="log-actions">
              <button 
                onClick={() => setAutoScroll(!autoScroll)}
                className={`auto-scroll-button ${autoScroll ? 'active' : ''}`}
                disabled={status.status === 'error' || status.status === 'closed'}
              >
                {autoScroll ? 'Auto-scroll On' : 'Auto-scroll Off'}
              </button>
              <button 
                onClick={() => toggleExpand(containerId)}
                className="expand-button"
              >
                {isExpanded ? 'Minimize' : 'Expand'}
              </button>
              <button 
                onClick={() => stopTail(file, containerId)}
                className="stop-button"
              >
                {status.status === 'error' || status.status === 'closed' ? 'Remove' : 'Stop'}
              </button>
              {(status.status === 'error' || status.status === 'closed') && (
                <button 
                  onClick={() => startTail(
                    serverId,
                    file.startsWith('/home') ? file : `/home/${servers[serverId]?.username}/.pm2/logs/${file}`
                  )}
                  className="reconnect-button"
                >
                  Retry
                </button>
              )}
            </div>
          </div>
          <pre
            ref={el => logContentRefs.current[containerId] = el}
            onScroll={(e) => handleLogScroll(containerId, e)}
            className="log-content"
          >
            {log || 'Waiting for data...'}
            {(status.status === 'error' || status.status === 'closed') && (
              <div className="stream-ended-message">
                --- Stream {status.status} at {new Date(status.timestamp).toLocaleTimeString()} ---
                {status.error && `\nError: ${status.error}`}
              </div>
            )}
          </pre>
        </div>
      );
    });
  };

  const handleTabSwitch = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  if (!isAuthReady) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <span>Initializing...</span>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div className="app">
      <div className="notification-container">
        {notifications.map(({ id, message, type }) => (
          <Notification
            key={id}
            message={message}
            type={type}
            onClose={() => removeNotification(id)}
          />
        ))}
      </div>
      <header className="app-header">
        <h1>Remote Service Manager</h1>
        <div className="server-selector">
          <div className="connection-status">
            <span className={`status-indicator ${connectionStatus}`}></span>
            <span className="status-text">{connectionStatus}</span>
          </div>
          <select 
            value={selectedServer} 
            onChange={async (e) => {
              const newServer = e.target.value;
              setSelectedServer(newServer);
              setPm2Processes([]); // Clear PM2 processes
              setSystemdServices([]); // Clear systemd services
              setDockerContainers([]); // Clear Docker containers
              
              if (newServer) {
                setIsLoading({ // Show loading state
                  pm2: true,
                  systemd: true,
                  docker: true
                });
                
                try {
                  // Fetch initial data for new server
                  if (activeTab === 'pm2') {
                    await fetchPM2Processes(newServer);
                  } else if (activeTab === 'systemd') {
                    await fetchSystemdServices(newServer);
                  } else if (activeTab === 'docker') {
                    await fetchDockerContainers(newServer);
                  }
                } catch (error) {
                  console.error('Failed to fetch initial data:', error);
                } finally {
                  setIsLoading({
                    pm2: false,
                    systemd: false,
                    docker: false
                  });
                }
              }
            }}
          >
            <option value="">Select a server</option>
            {Object.entries(servers).map(([id, server]) => (
              <option key={id} value={id}>
                {id} ({server.host}) - {server.online ? '🟢 Online' : '🔴 Offline'} 
                ({server.activeStreams || 0} streams)
              </option>
            ))}
          </select>
          <button onClick={fetchServers} disabled={isLoading.pm2 || isLoading.systemd || isLoading.docker}>
            {isLoading.pm2 || isLoading.systemd || isLoading.docker ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </header>

      <div className="main-content">
        <div className="services-section">
          <div className="tab-selector">
            <button 
              className={`tab ${activeTab === 'pm2' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('pm2')}
            >
              PM2 Processes ({pm2Processes.length})
            </button>
            <button 
              className={`tab ${activeTab === 'docker' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('docker')}
            >
              Docker ({dockerContainers.length})
            </button>
            <button 
              className={`tab ${activeTab === 'systemd' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('systemd')}
            >
              Systemd Services ({systemdServices.length})
            </button>
          </div>

          <div className="service-group">
            {(isLoading.pm2 && activeTab === 'pm2') || (isLoading.systemd && activeTab === 'systemd') || (isLoading.docker && activeTab === 'docker') ? (
              <div className="loading">
                <div className="spinner"></div>
                <span>Loading {activeTab === 'pm2' ? 'PM2 processes' : activeTab === 'systemd' ? 'systemd services' : 'Docker containers'}...</span>
              </div>
            ) : (
              <div className="service-list">
                {activeTab === 'pm2' ? (
                  pm2Processes.length > 0 ? (
                    pm2Processes.map((process) => (
                      <div key={process.pm_id} className="service-item">
                        <div className="service-info">
                          <strong>{process.name}</strong>
                          <div className="service-info-details">
                            <span className={`status ${process.status}`}>
                              {process.status}
                            </span>
                            <span><strong>CPU:</strong> {process.cpu}%</span>
                            <span><strong>Memory:</strong> {Math.round(process.memory / 1024 / 1024)}MB</span>
                            <span><strong>Last Restart:</strong> {process.uptime ? new Date(process.uptime).toLocaleString() : 'Never'}</span>
                            <span><strong>Restart Count:</strong> {process.restartCount || 0}</span>
                          </div>
                        </div>
                        {renderPm2Actions(process)}
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      {selectedServer ? 'No PM2 processes found' : 'Select a server to view processes'}
                    </div>
                  )
                ) : activeTab === 'systemd' ? (
                  systemdServices.length > 0 ? (
                    systemdServices.map((service, index) => (
                      <div key={index} className="service-item">
                        <div className="service-info">
                          <strong>{service.name}</strong>
                          <span className={`status ${service.active}`}>
                            {service.active}
                          </span>
                          <span>{service.description}</span>
                        </div>
                        <div className="service-actions">
                          <button 
                            onClick={() => restartSystemdService(service.name)}
                            disabled={operationInProgress.has(`restart-systemd-${service.name}`)}
                            className={operationInProgress.has(`restart-systemd-${service.name}`) ? 'loading' : ''}
                          >
                            {operationInProgress.has(`restart-systemd-${service.name}`) ? 
                              'Restarting...' : 'Restart'
                            }
                          </button>
                          <button 
                            onClick={() => startTail(selectedServer, '/var/log/syslog')}
                            className="log-button"
                          >
                            Tail Syslog
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      {selectedServer ? 'No systemd services found' : 'Select a server to view services'}
                    </div>
                  )
                ) : activeTab === 'docker' ? (
                  dockerContainers.length > 0 ? (
                    dockerContainers.map((container) => (
                      <div key={container.id} className="service-item">
                        <div className="service-info">
                          <strong>{container.name}</strong>
                          <div className="service-info-details">
                            <span className={`status ${container.state}`}>
                              {container.state}
                            </span>
                            <span><strong>Image:</strong> {container.image}</span>
                            <span><strong>Created:</strong> {new Date(container.created).toLocaleString()}</span>
                            <span><strong>Status:</strong> {container.status}</span>
                          </div>
                        </div>
                        {renderDockerActions(container)}
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      {selectedServer ? 'No Docker containers found' : 'Select a server to view containers'}
                    </div>
                  )
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div className="status-panel">
          <h2>Service Status</h2>
          <div className="status-info">
            <p>Active Streams: {activeTails.size}/5</p>
            <p>Connection Status: {connectionStatus}</p>
            <p>Active Operations: {operationInProgress.size}</p>
          </div>
        </div>

        <div className="logs-section">
          <h2>Live Logs ({activeTails.size}/5 active streams)</h2>
          <div ref={logContainerRef} className={`log-container ${expandedLog ? 'expanded' : ''}`}>
            {Object.keys(logs).length > 0 ? (
              renderLogs()
            ) : (
              <div className="empty-state">
                No active log streams. Click "Tail Logs" on any service to start monitoring.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;